import { DefectCode } from "../../types/defect";
import { Button } from "primereact/button";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { useAppSelector } from "../../hooks/storeHooks";
import { selectDefectCodes, selectDefectCodeCategories } from "../../store/selectors/defectSelectors";
import { GenericCodeReasonDialog } from "./GenericCodeReasonDialog";
import { mapCategories } from "../../config/CategoryMapping";
import { getSuggestedCodes } from "../../services/suggestedCodesService";
import { warningToast } from "@idwal/idwal-react-components";

type Props = {
  onHide: () => void;
  onSubmit: (code: string | null, codeAssignReason?: string | null, suggestedCodes?: string[]) => Promise<void>;
  visible: boolean;
  currentTypeCode?: string | null;
  category?: string;
  description?: string;
  defectId?: string;
};

type SuggestedCode = {
  defect_code: string;
  confidence_score: number;
  description?: string;
  category?: string;
};

export const DefectCodeDialog = (props: Props) => {
  const { onHide, onSubmit, visible, currentTypeCode, category, description, defectId } = props;

  const [selectedDefectCode, setSelectedDefectCode] = useState<DefectCode | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingSuggested, setLoadingSuggested] = useState<boolean>(false);
  const [showReasonDialog, setShowReasonDialog] = useState<boolean>(false);
  const [mode, setMode] = useState<"suggested" | "browse">("suggested");
  const [suggestedCodes, setSuggestedCodes] = useState<SuggestedCode[]>([]);
  const [suggestedCodesFetched, setSuggestedCodesFetched] = useState<boolean>(false);
  const [selectedSuggestedCode, setSelectedSuggestedCode] = useState<SuggestedCode | null>(null);
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    code: { value: null, matchMode: FilterMatchMode.CONTAINS },
    category: { value: null, matchMode: FilterMatchMode.CONTAINS },
    subcategory: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const [first, setFirst] = useState<number>(0);
  const [rows, setRows] = useState<number>(50);
  const tableRef = useRef<DataTable<DefectCode[]>>(null);
  const fetchInProgressRef = useRef<boolean>(false);

  const defectCodes = useAppSelector(selectDefectCodes);
  const categories = useAppSelector(selectDefectCodeCategories);
  const cleanedDefectCodes = useMemo(() => 
    defectCodes.map(item => ({
      ...item,
      category: item.category ?? "",
      subCategory: item.subCategory ?? ""
    })), [defectCodes]);
  const categoryOptions = useMemo(() => mapCategories(categories), [categories]);

  // Fetch suggested codes from service
  const fetchSuggestedCodes = useCallback(async (force = false) => {
    console.log('fetchSuggestedCodes called', { category, description, force });
    
    if (fetchInProgressRef.current) {
      return;
    }
    
    if (!force && (!category || !description)) {
      console.log('Missing category or description, skipping fetch');
      setSuggestedCodes([]);
      setSuggestedCodesFetched(true);
      return;
    }

    fetchInProgressRef.current = true;
    setLoadingSuggested(true);
    const response = await getSuggestedCodes(category || '', description || '', defectId || '0');
    
    console.log('getSuggestedCodes response:', response);
    
    if (!response || !response.defects[0]?.defect_code || response.defects[0].defect_code.length === 0) {
      warningToast("No suggested codes found for this defect. Please browse all codes to assign one manually.");
      setSuggestedCodes([]);
      setLoadingSuggested(false);
      setSuggestedCodesFetched(true);
      fetchInProgressRef.current = false;
      setMode("browse");
      return;
    }

    // Transform to flat list and lookup details
    const codes: SuggestedCode[] = [];
    response.defects.forEach(defect => {
      defect.defect_code.forEach((code, index) => {
        const defectDetails = defectCodes.find(d => d.code === code);
        codes.push({
          defect_code: code,
          confidence_score: defect.confidence_scores[index],
          description: defectDetails?.shortGenName,
          category: defectDetails?.category
        });
      });
    });
    
    console.log('Transformed codes:', codes);
    
    setSuggestedCodes(codes);
    setLoadingSuggested(false);
    setSuggestedCodesFetched(true);
    fetchInProgressRef.current = false;
  }, [category, description, defectCodes]);

  function getCodeCategory(code: string) {
    if (categories && categories[code]) {
      return categories[code];
    }

    return "General";
  }

  function getCategoryPrefix(categoryName: string) {
    // Find the code prefix for the given category name
    const entry = Object.entries(categories).find(([_, cat]) => cat.toLowerCase() === categoryName.toLowerCase());
    return entry ? entry[0].split('.')[0] : null;
  }

  // Auto-select filter based on current typeCode
  useEffect(() => {
    if (visible) {
      // Reset filters first
      let _filters = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        code: { value: null, matchMode: FilterMatchMode.CONTAINS },
        category: { value: null, matchMode: FilterMatchMode.CONTAINS },
        subcategory: { value: null, matchMode: FilterMatchMode.CONTAINS },
      };
      
      // Auto-select filter if we have a valid typeCode with area prefix
      if (currentTypeCode && currentTypeCode.includes('.')) {
        const matchingCode = cleanedDefectCodes.find(dc => dc.code === currentTypeCode);
        if (matchingCode) {
          const codeCategory = getCodeCategory(currentTypeCode);
          // @ts-ignore
          _filters["category"].value = codeCategory;
          // @ts-ignore
          _filters["code"].value = currentTypeCode.split('.')[0];
        }
      } else if (category) {
        // If no typeCode but we have a category, use it for filtering
        // @ts-ignore
        _filters["code"].value = getCategoryPrefix(category);
      }
      
      setFilters(_filters);
      setGlobalFilterValue("");
      
      // Auto-select the current defect code if it exists
      if (currentTypeCode && currentTypeCode.trim() !== "") {
        const matchingCode = cleanedDefectCodes.find(dc => dc.code === currentTypeCode);
        setSelectedDefectCode(matchingCode || null);
        
        // Calculate which page the selected code is on (after filtering)
        if (matchingCode) {
          // Apply the same filters to get filtered data
          let filteredData = cleanedDefectCodes;
          const codeValue = _filters.code.value as string | null;
          const categoryValue = _filters.category.value as string | null;
          
          if (codeValue) {
            filteredData = filteredData.filter(dc => dc.code.toLowerCase().includes(codeValue.toLowerCase()));
          }
          if (categoryValue) {
            filteredData = filteredData.filter(dc => dc.category.toLowerCase().includes(categoryValue.toLowerCase()));
          }
          
          const index = filteredData.indexOf(matchingCode);
          if (index >= 0) {
            const rowsPerPage = 50;
            const pageIndex = Math.floor(index / rowsPerPage);
            setFirst(pageIndex * rowsPerPage);
          } else {
            setFirst(0);
          }
        } else {
          setFirst(0);
        }
      } else {
        setSelectedDefectCode(null);
        setFirst(0);
      }
      
      // Reset suggested codes fetched flag
      setSuggestedCodesFetched(false);
      
      // Switch to browse mode if no description, no category, or if defect code already exists (unless it's a placeholder .999 code)
      const isPlaceholderCode = currentTypeCode && currentTypeCode.trim().endsWith(".999");
      const shouldBypassSuggested = !category || (currentTypeCode && currentTypeCode.trim() !== "" && !isPlaceholderCode);
      
      if (!description || shouldBypassSuggested) {
        setMode("browse");
      } else {
        setMode("suggested");
        fetchSuggestedCodes();
      }
    }
  }, [currentTypeCode, visible, description, category, cleanedDefectCodes]);

  // Scroll to selected row after table renders
  useEffect(() => {
    if (visible && selectedDefectCode && tableRef.current) {
      setTimeout(() => {
        const selectedRow = document.querySelector('.p-datatable-tbody .p-highlight');
        if (selectedRow) {
          selectedRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [visible, selectedDefectCode, first]);

  const onGlobalFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      let _filters = { ...filters };

      // @ts-ignore
      _filters["global"].value = value;

      setFilters(_filters);
      setGlobalFilterValue(value);
    },
    [filters]
  );

  const onCategoryFilterChange = useCallback(
    (value: string | null) => {
      const _filters = { ...filters };
      _filters.code = { value, matchMode: FilterMatchMode.CONTAINS };
      _filters.category = { value: null, matchMode: FilterMatchMode.CONTAINS };
      _filters.subcategory = { value: null, matchMode: FilterMatchMode.CONTAINS };
      setFilters(_filters);
    },
    [filters]
  );

  const isGeneralCode = useCallback((code: string) => {
    return code.includes(".9");
  }, []);

  const onAssign = useCallback(async () => {
    const code = mode === "suggested" ? selectedSuggestedCode?.defect_code : selectedDefectCode?.code;
    if (!code) return;
    
    if (isGeneralCode(code)) {
      setShowReasonDialog(true);
      return;
    }
    
    setLoading(true);
    
    // Pass suggested codes if any were fetched, regardless of which mode user is in
    const suggestedCodesList = suggestedCodes.length > 0 ? suggestedCodes.map(sc => sc.defect_code) : undefined;
    await onSubmit(code, null, suggestedCodesList);
    
    setLoading(false);
    onHide();
  }, [mode, selectedSuggestedCode, selectedDefectCode, suggestedCodes, isGeneralCode, onSubmit, onHide]);

  const onReasonSubmit = useCallback(async (reason: string) => {
    const code = mode === "suggested" ? selectedSuggestedCode?.defect_code : selectedDefectCode?.code;
    if (!code) return;
    
    setShowReasonDialog(false);
    setLoading(true);
    
    // Pass suggested codes if any were fetched, regardless of which mode user is in
    const suggestedCodesList = suggestedCodes.length > 0 ? suggestedCodes.map(sc => sc.defect_code) : undefined;
    await onSubmit(code, reason, suggestedCodesList);
    
    setLoading(false);
    onHide();
  }, [mode, selectedSuggestedCode, selectedDefectCode, suggestedCodes, onSubmit, onHide]);

  const onReasonCancel = useCallback(() => {
    setShowReasonDialog(false);
  }, []);

  const categoryFilterValue = useMemo(() => {
    const categoryFilter = filters.code;
    return categoryFilter && 'value' in categoryFilter ? categoryFilter.value : null;
  }, [filters.code]);

  const headerContent = useMemo(
    () => (
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', justifyContent: 'space-between' }}>
        {mode === "browse" ? (
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Search"
          />
        ) : (
          <div>
            <div style={{fontSize: '1.2rem', color: 'var(--text-color-secondary)'}}> Suggested codes below are based on the defect category and description</div>
            <div style={{fontSize: '1.1rem', opacity: 0.8}}>Select one to assign, or browse all codes</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {mode === "browse" && (
            <>
              <Dropdown
                value={categoryFilterValue}
                options={categoryOptions}
                onChange={(e) => onCategoryFilterChange(e.value)}
                filter
                showClear
                placeholder="Search by Category"
                scrollHeight="400px"
                optionLabel="label"
                optionValue="value"
              />
              <Button 
                label="Suggested Codes" 
                outlined
                disabled={!category}
                style={{ 
                  color: '#009eb2',
                  boxShadow: 'inset 0 0 0 1px rgba(40, 35, 91, 0.3)'
                }}
                onClick={() => {
                  setMode("suggested");
                  if (!suggestedCodesFetched && !loadingSuggested) {
                    fetchSuggestedCodes(true);
                  }
                }}
              />
            </>
          )}
          {mode === "suggested" && (
            <Button 
              label="Browse All Codes" 
              outlined
              style={{ 
                color: '#009eb2',
                boxShadow: 'inset 0 0 0 1px rgba(40, 35, 91, 0.3)'
              }}
              onClick={() => setMode("browse")}
            />
          )}
        </div>
      </div>
    ),
    [globalFilterValue, categoryFilterValue, categoryOptions, onGlobalFilterChange, onCategoryFilterChange, mode]
  );

  const footerContent = useMemo(
    () => (
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
        <Button
          label="Cancel"
          onClick={onHide}
          text
          style={{ width: '200px' }}
        />
        <Button
          label="Assign Code"
          onClick={onAssign}
          style={{ width: '200px' }}
          disabled={mode === "suggested" ? !selectedSuggestedCode : !selectedDefectCode}
          loading={loading}
        />
      </div>
    ),
    [mode, selectedSuggestedCode, selectedDefectCode, loading, onAssign, onHide]
  );

  return (
    <>
      <Dialog
        header={"Assign a Defect Code"}
        visible={visible}
        className="large-dialog"
        draggable={false}
        blockScroll
        footer={footerContent}
        onHide={onHide}
        style={{ maxHeight: '95vh' }}
        contentStyle={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {mode === "browse" ? (
          <DataTable
            ref={tableRef}
            value={cleanedDefectCodes}
            selection={selectedDefectCode!}
            header={headerContent}
            globalFilterFields={["code", "shortGenName"]}
            filters={filters}
            emptyMessage={"No data found"}
            onSelectionChange={(e) => setSelectedDefectCode(e.value)}
            paginator
            rows={rows}
            rowsPerPageOptions={[10, 25, 50]}
            scrollable
            scrollHeight="calc(100vh - 350px)"
            first={first}
            onPage={(e) => {
              setFirst(e.first);
              setRows(e.rows);
            }}
          >
            <Column selectionMode="single" headerStyle={{ width: "3rem" }}></Column>
            <Column
              key="code"
              sortable
              sortField="code"
              field="code"
              header="Defect Code"
            />
            <Column
              key="shortGenName"
              sortable
              sortField="shortGenName"
              field="shortGenName"
              header="Description"
            />
            <Column
              key="category"
              sortable
              sortField="category"
              field="category"
              header="Category"
            />
          </DataTable>
        ) : loadingSuggested ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <ProgressSpinner />
          </div>
        ) : (
          <DataTable
            value={suggestedCodes}
            selection={selectedSuggestedCode!}
            header={headerContent}
            emptyMessage={"No suggested codes found"}
            onSelectionChange={(e) => {
              setSelectedSuggestedCode(e.value);
            }}
            scrollable
            scrollHeight="calc(100vh - 350px)"
          >
            <Column selectionMode="single" headerStyle={{ width: "3rem" }}></Column>
            <Column
              field="defect_code"
              header="Defect Code"
            />
            <Column
              field="description"
              header="Description"
            />
            <Column
              field="category"
              header="Category"
            />
          </DataTable>
        )}
      </Dialog>
      
      <GenericCodeReasonDialog
        visible={showReasonDialog}
        onHide={onReasonCancel}
        onSubmit={onReasonSubmit}
        defectCode={selectedDefectCode?.code || ""}
      />
    </>
  );
};
